import { lazy, type ComponentType } from 'react';
import type { LazyView } from './types';

/**
 * Helper sur `React.lazy` qui gère les **exports nommés** (vs default-only).
 *
 * @remarks
 * `React.lazy` natif n'accepte qu'un module avec `export default`. Beaucoup
 * de nos views utilisent des exports nommés (ex: `export function MyView()`)
 * pour la cohérence et le tree-shaking. Ce helper fait la conversion.
 *
 * @example
 * ```ts
 * // src/features/skill-up/views/LearningSessionView.tsx
 * export function LearningSessionView() { ... }
 *
 * // src/features/skill-up/manifest.ts
 * import { lazyView } from '@/app/registry';
 *
 * centerView: {
 *   [CONTEXTS.SKILL_UP_SESSION]: lazyView(
 *     () => import('./views/LearningSessionView'),
 *     'LearningSessionView',
 *   ),
 * }
 * ```
 *
 * @param loader - Fonction d'import dynamique qui retourne le module
 * @param exportName - Nom de l'export à extraire (typé selon les exports du module)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyView<T extends Record<string, ComponentType<any>>>(
  loader: () => Promise<T>,
  exportName: keyof T & string,
): LazyView {
  return lazy(async () => {
    const mod = await loader();
    const Component = mod[exportName];
    if (!Component) {
      throw new Error(
        `lazyView: export "${exportName}" introuvable dans le module. ` +
          `Vérifie le nom de l'export et le chemin d'import.`,
      );
    }
    return { default: Component };
  }) as LazyView;
}
